import styled, { css } from 'styled-components'

import { theme } from '../../constants'

export const PracticeCollage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: inherit;
  overflow: hidden;

  &::after {
    content: 'AH—AH—AH';
    position: absolute;
    right: -17px;
    bottom: 15px;
    z-index: 4;
    padding: 5px 10px;
    color: ${theme.colors.textPrimary};
    background: ${theme.colors.white};
    border: 1px solid ${theme.colors.textPrimary};
    font-size: 9px;
    font-weight: ${theme.typography.weight.bold};
    letter-spacing: 0.16em;
    transform: rotate(-6deg);
  }

  > .collage-icon {
    position: absolute;
    top: 20px;
    right: 22px;
    z-index: 5;
    width: 48px;
    height: 48px;
    display: grid;
    place-items: center;
    color: ${theme.colors.white};
    background: ${theme.colors.brandCoral};
    border: 3px solid ${theme.colors.textPrimary};
    border-radius: ${theme.radii.round};
    box-shadow: ${theme.shadows.large};
    font-size: 20px;
    transform: rotate(9deg);
  }
`

export const BannerDecoration = styled.i<{
  $position: 'one' | 'two' | 'three'
}>`
  position: absolute;
  width: ${({ $position }) => ($position === 'two' ? '160px' : '110px')};
  height: ${({ $position }) => ($position === 'two' ? '160px' : '110px')};
  top: ${({ $position }) =>
    $position === 'one' ? '-36px' : $position === 'two' ? '130px' : '-42px'};
  left: ${({ $position }) =>
    $position === 'one' ? '18px' : $position === 'two' ? '120px' : '230px'};
  background: ${({ $position }) =>
    $position === 'one'
      ? theme.colors.brandCoral
      : $position === 'two'
        ? theme.colors.brandBlue
        : theme.colors.white};
  border-radius: 32%;
  opacity: ${({ $position }) => ($position === 'three' ? 0.48 : 0.9)};
  transform: rotate(35deg);
`

export const CollagePhoto = styled.div<{ $position: 'back' | 'front' }>`
  position: absolute;
  z-index: ${({ $position }) => ($position === 'front' ? 3 : 2)};
  width: ${({ $position }) => ($position === 'front' ? '148px' : '126px')};
  height: ${({ $position }) => ($position === 'front' ? '205px' : '170px')};
  right: ${({ $position }) => ($position === 'front' ? '76px' : '185px')};
  bottom: ${({ $position }) => ($position === 'front' ? '-18px' : '26px')};
  overflow: hidden;
  background: ${theme.colors.backgroundLight};
  border: 5px solid ${theme.colors.white};
  box-shadow: ${theme.shadows.large};
  clip-path: ${({ $position }) =>
    $position === 'front'
      ? 'polygon(4% 2%, 97% 0, 100% 94%, 8% 100%, 0 12%)'
      : 'polygon(0 8%, 91% 0, 100% 91%, 9% 100%)'};
  transform: ${({ $position }) =>
    $position === 'front' ? 'rotate(4deg)' : 'rotate(-8deg)'};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
    filter: ${({ $position }) =>
      $position === 'back' ? 'grayscale(1) contrast(1.05)' : 'saturate(0.9)'};
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ $position }) =>
      $position === 'back'
        ? theme.effects.collageWash
        : `linear-gradient(transparent, ${theme.effects.scrim})`};
    pointer-events: none;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    width: ${({ $position }) => ($position === 'front' ? '124px' : '106px')};
    height: ${({ $position }) => ($position === 'front' ? '168px' : '140px')};
    right: ${({ $position }) => ($position === 'front' ? '20%' : '49%')};
    bottom: ${({ $position }) => ($position === 'front' ? '-28px' : '8px')};
  }
`

export const CollageTape = styled.i`
  position: absolute;
  top: 23px;
  left: 38%;
  z-index: 4;
  width: 74px;
  height: 22px;
  background: ${theme.effects.surfaceSoft};
  box-shadow: 0 1px 3px ${theme.effects.shadowOverlay};
  clip-path: polygon(2% 8%, 98% 0, 94% 95%, 0 100%);
  transform: rotate(-10deg);
`

export const MiniStats = styled.section`
  position: relative;
  padding: ${theme.spacing[6]};
  overflow: hidden;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xlarge};
  box-shadow: ${theme.shadows.small};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${theme.spacing[6]};
    width: 72px;
    height: 4px;
    background: ${theme.roleColors.student.main};
    border-radius: 0 0 ${theme.radii.pill} ${theme.radii.pill};
  }
`

export const SummaryHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};

  span {
    color: ${theme.roleColors.student.strong};
    font-size: ${theme.typography.size.caption};
    font-weight: ${theme.typography.weight.bold};
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  h2 {
    margin: ${theme.spacing[1]} 0 0;
    font-size: ${theme.typography.size.h2};
    line-height: ${theme.typography.lineHeight.heading};
  }

  p {
    margin: ${theme.spacing[1]} 0 0;
    color: ${theme.colors.textMuted};
    font-size: ${theme.typography.size.bodySmall};
  }
`

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${theme.spacing[4]};
`

export const ProgressInsights = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`

export const ProgressBody = styled.div<{ $compact?: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(290px, 0.72fr);
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;

    ${({ $compact }) =>
      $compact &&
      css`
        grid-template-columns: repeat(2, minmax(0, 1fr));

        ${ProgressInsights},
        ${StatsGrid} {
          display: contents;
        }
      `}
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;

    ${({ $compact }) =>
      $compact &&
      css`
        ${ProgressInsights},
        ${StatsGrid} {
          display: grid;
        }
      `}
  }
`

export const ProgressStatus = styled.p`
  margin: 0 0 ${theme.spacing[4]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  color: ${theme.roleColors.student.text};
  background: ${theme.roleColors.student.soft};
  border: 1px solid ${theme.roleColors.student.strong};
  border-radius: ${theme.radii.large};
  font-size: ${theme.typography.size.bodySmall};
  line-height: ${theme.typography.lineHeight.body};
  text-align: center;
`

export const MiniStat = styled.article`
  position: relative;
  min-height: 132px;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding-block: ${theme.spacing[4]};
  padding-inline-start: ${theme.spacing[0]};
  padding-inline-end: ${theme.spacing[2]};
  overflow: hidden;
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.large};
  transition:
    transform ${theme.motion.normal} ${theme.motion.easing},
    border-color ${theme.motion.normal} ${theme.motion.easing};

  .stat-copy {
    position: relative;
    z-index: 1;
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
  }

  .stat-copy strong {
    color: ${theme.colors.textPrimary};
    font-size: ${theme.typography.size.h2};
    line-height: ${theme.typography.lineHeight.heading};
  }

  .stat-copy small {
    color: ${theme.colors.textMuted};
    font-size: ${theme.typography.size.caption};
    line-height: ${theme.typography.lineHeight.compact};
  }

  &:hover {
    border-color: ${theme.roleColors.student.main};
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const StatArtwork = styled.div`
  width: 112px;
  height: 96px;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-start;
  pointer-events: none;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
    object-position: left center;
  }
`

export const TrendCard = styled.article`
  position: relative;
  min-width: 0;
  overflow: hidden;
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.large};
  box-shadow: ${theme.shadows.small};

  &::before,
  &::after {
    content: '';
    position: absolute;
    pointer-events: none;
  }

  &::before {
    top: -28px;
    right: 54px;
    width: 92px;
    height: 64px;
    background: ${theme.colors.brandCoralLight};
    transform: rotate(8deg);
  }

  &::after {
    top: 20px;
    right: 24px;
    width: 18px;
    height: 18px;
    background: ${theme.colors.brandBlue};
    border-radius: ${theme.radii.round};
    opacity: 0.72;
  }
`

export const TrendHeader = styled.header`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  color: ${theme.colors.textPrimary};

  div {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing[1]};
  }

  strong {
    font-size: ${theme.typography.size.bodySmall};
  }

  span,
  small {
    color: ${theme.colors.textMuted};
    font-size: ${theme.typography.size.caption};
  }

  small {
    padding: 6px 10px;
    color: ${theme.roleColors.student.text};
    background: ${theme.roleColors.student.soft};
    border-radius: ${theme.radii.pill};
    font-weight: ${theme.typography.weight.semibold};
  }
`

export const TrendBars = styled.div`
  position: relative;
  z-index: 1;
  height: 164px;
  display: flex;
  align-items: stretch;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[4]};
  padding-top: ${theme.spacing[3]};
  background: repeating-linear-gradient(
    to top,
    transparent 0,
    transparent 31px,
    ${theme.colors.border} 32px
  );
  border-top: 1px solid ${theme.colors.border};
  border-bottom: 1px solid ${theme.colors.borderStrong};

  & > div {
    min-width: 0;
    display: flex;
    align-items: center;
    flex: 1;
    flex-direction: column;
    justify-content: flex-end;
    gap: ${theme.spacing[2]};
  }

  & > div > span {
    position: relative;
    width: min(32px, 72%);
    min-height: 8px;
    background: ${theme.roleColors.student.main};
    border: 1px solid ${theme.roleColors.student.strong};
    border-top-left-radius: ${theme.radii.pill};
    border-top-right-radius: ${theme.radii.pill};
    border-bottom-right-radius: ${theme.radii.small};
    border-bottom-left-radius: ${theme.radii.small};
    box-shadow: ${theme.shadows.small};
    transition: height ${theme.motion.slow} ${theme.motion.easing};
  }

  & > div[data-empty='true'] > span {
    width: 8px;
    height: 8px !important;
    min-height: 8px;
    background: ${theme.colors.borderStrong};
    border: 0;
    border-radius: ${theme.radii.round};
    box-shadow: none;
  }

  & > div > span::before {
    content: '';
    position: absolute;
    left: 50%;
    top: -1px;
    width: calc(100% + 2px);
    height: 7px;
    background: ${theme.colors.brandCoral};
    border-radius: ${theme.radii.pill};
    transform: translateX(-50%);
  }

  & > div[data-empty='true'] > span::before {
    display: none;
  }

  b {
    color: ${theme.colors.textPrimary};
    font-size: ${theme.typography.size.caption};
    font-weight: ${theme.typography.weight.bold};
  }

  small {
    max-width: 100%;
    overflow: hidden;
    color: ${theme.colors.textMuted};
    font-size: ${theme.typography.size.caption};
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (prefers-reduced-motion: reduce) {
    & > div > span {
      transition: none;
    }
  }
`

export const GamificationCard = styled.article`
  position: relative;
  min-width: 0;
  min-height: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  overflow: hidden;
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.large};
  transition:
    transform ${theme.motion.normal} ${theme.motion.easing},
    border-color ${theme.motion.normal} ${theme.motion.easing};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${theme.spacing[4]};
    width: 72px;
    height: 4px;
    background: ${theme.roleColors.student.main};
    border-radius: 0 0 ${theme.radii.pill} ${theme.radii.pill};
  }

  &:hover {
    border-color: ${theme.roleColors.student.main};
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const GamificationContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`

export const GamificationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};

  > .gamification-trophy {
    width: 52px;
    height: 52px;
    display: grid;
    flex: 0 0 auto;
    place-items: center;
    color: ${theme.roleColors.student.text};
    background: ${theme.roleColors.student.main};
    border-radius: ${theme.radii.medium};
    font-size: ${theme.typography.size.bodyLarge};
  }

  > .gamification-title {
    min-width: 0;
    display: flex;
    flex: 1;
    flex-direction: column;
  }

  small {
    color: ${theme.roleColors.student.strong};
    font-size: ${theme.typography.size.caption};
    font-weight: ${theme.typography.weight.bold};
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  strong {
    color: ${theme.colors.textPrimary};
    font-size: ${theme.typography.size.body};
    line-height: ${theme.typography.lineHeight.body};
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const GamificationMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.size.caption};

  strong {
    color: ${theme.roleColors.student.strong};
    text-align: right;
  }
`

export const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${theme.spacing[4]};
`

export const LearningStatus = styled.p`
  margin: 0;
  padding: ${theme.spacing[6]};
  color: ${theme.colors.textMuted};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.large};
  font-size: ${theme.typography.size.bodySmall};
  text-align: center;
  margin-top: ${theme.spacing[4]};
`

export const LearningResourcesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${theme.spacing[4]};
`

export const LearningResourceCard = styled.button`
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  overflow: hidden;
  color: inherit;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.large};
  box-shadow: ${theme.shadows.small};
  text-align: left;
  cursor: pointer;
  transition:
    transform ${theme.motion.normal} ${theme.motion.easing},
    box-shadow ${theme.motion.normal} ${theme.motion.easing};

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${theme.shadows.medium};
  }

  &:focus-visible {
    outline: 3px solid ${theme.roleColors.student.focus};
    outline-offset: 3px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.72;
    transform: none;
  }

  &:disabled:hover {
    box-shadow: ${theme.shadows.small};
  }

  .resource-image {
    position: relative;
    aspect-ratio: 16 / 8;
    height: auto;
    overflow: hidden;
    background: ${theme.roleColors.student.soft};
    border-radius: ${theme.radii.large};

    &::after {
      content: '';
      position: absolute;
      top: 14px;
      right: 18px;
      width: 42px;
      height: 12px;
      background: ${theme.semanticColors.background.surface};
      transform: rotate(7deg);
      pointer-events: none;
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .resource-title {
    position: absolute;
    bottom: ${theme.spacing[3]};
    left: ${theme.spacing[3]};
    padding: 7px 10px;
    color: ${theme.colors.black};
    background: ${theme.roleColors.student.main};
    border-radius: ${theme.radii.pill};
    font-size: ${theme.typography.size.bodySmall};
    font-weight: ${theme.typography.weight.bold};
    box-shadow: ${theme.shadows.small};
  }

  .resource-description {
    height: 76px;
    margin: 0;
    padding: ${theme.spacing[3]};
    box-sizing: border-box;
    display: flex;
    align-items: center;
    color: ${theme.colors.textMuted};
    font-size: ${theme.typography.size.bodySmall};
    line-height: ${theme.typography.lineHeight.body};
  }
`

export const CourseCard = styled.article`
  overflow: hidden;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.large};
  box-shadow: ${theme.shadows.small};
  transition:
    transform ${theme.motion.normal} ${theme.motion.easing},
    box-shadow ${theme.motion.normal} ${theme.motion.easing};

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${theme.shadows.medium};
  }
`

export const CourseImage = styled.div`
  position: relative;
  height: 150px;
  margin: ${theme.spacing[2]};
  overflow: hidden;
  background: ${theme.colors.backgroundGrey};
  border-radius: ${theme.radii.medium};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${theme.motion.slow} ${theme.motion.easing};
  }

  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: 50%;
    z-index: 2;
    width: 44px;
    height: 12px;
    background: ${theme.effects.surfaceSoft};
    box-shadow: 0 1px 2px ${theme.effects.scrim};
    clip-path: polygon(3% 12%, 100% 0, 95% 88%, 0 100%);
    opacity: 0;
    transform: translateX(-50%) rotate(-2deg);
    transition: opacity ${theme.motion.normal} ${theme.motion.easing};
  }

  ${CourseCard}:hover & img {
    transform: scale(1.035);
  }

  ${CourseCard}:hover &::after {
    opacity: 1;
  }

  > button {
    position: absolute;
    right: ${theme.spacing[3]};
    bottom: ${theme.spacing[3]};
    width: 44px;
    height: 44px;
    display: grid;
    place-items: center;
    padding-left: 3px;
    color: ${theme.roleColors.student.text};
    background: ${theme.roleColors.student.main};
    border: 0;
    border-radius: ${theme.radii.round};
    box-shadow: ${theme.shadows.medium};
    cursor: pointer;
  }
`

export const LevelPill = styled.span`
  position: absolute;
  top: ${theme.spacing[3]};
  left: ${theme.spacing[3]};
  padding: 6px 9px;
  color: ${theme.colors.textPrimary};
  background: ${theme.effects.surfaceActive};
  border-radius: ${theme.radii.pill};
  backdrop-filter: blur(8px);
  font-size: ${theme.typography.size.caption};
  font-weight: ${theme.typography.weight.bold};
`

export const CourseInfo = styled.div`
  padding: ${theme.spacing[3]} ${theme.spacing[4]} ${theme.spacing[4]};

  h3 {
    margin: 0;
    color: ${theme.colors.textPrimary};
    font-size: ${theme.typography.size.body};
    line-height: ${theme.typography.lineHeight.heading};
  }

  p {
    margin: ${theme.spacing[1]} 0 ${theme.spacing[3]};
    color: ${theme.colors.textMuted};
    font-size: ${theme.typography.size.caption};
  }
`

export const CourseMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[2]};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.size.caption};
`

export const InstructorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${theme.spacing[4]};
`

export const TeamStatus = styled.p`
  margin: 0;
  padding: ${theme.spacing[6]};
  color: ${theme.colors.textMuted};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.large};
  font-size: ${theme.typography.size.bodySmall};
  text-align: center;
`

export const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(${theme.spacing[6]}, 4vw, ${theme.spacing[8]});

  @media (max-width: ${theme.breakpoints.desktop}) {
    ${GamificationCard} {
      min-height: 180px;
    }
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    ${CollageTape} {
      left: 32%;
    }

    ${CoursesGrid},
    ${InstructorsGrid} {
      grid-template-columns: 1fr;
    }

    ${LearningResourcesGrid} {
      grid-template-columns: 1fr;
    }

    ${MiniStats} {
      padding: ${theme.spacing[4]};
    }

    ${SummaryHeader} {
      align-items: flex-start;
      flex-direction: column;
      margin-bottom: ${theme.spacing[4]};
    }

    ${TrendHeader} {
      flex-direction: column;
    }
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    ${StatsGrid} {
      grid-template-columns: 1fr;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    ${CourseCard},
    ${CourseImage} img {
      transition: none;
    }
  }
`
